import React, { useRef, useState } from "react";

import { Info } from "@mui/icons-material";
import {
  Tooltip,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { IAlertGroup } from "@/types/typesAlerts";

import {
  BulletinAccessState,
  NotifyState,
  BulletinAccessAction,
  NotifyAction,
  PortalAccessCheckboxStatus,
  potentialGroupsState,
} from "./types";

interface NotifyEmailTableProps {
  bulletinAccessState: BulletinAccessState;
  notifyState: NotifyState;
  potentialGroups: potentialGroupsState;
  setBulletinAccessStateDispatch: (action: BulletinAccessAction) => void;
  setNotifyStateDispatch: (action: NotifyAction) => void;
  changePortalAccess: (group_id: number, time_expires: Date | null) => void;
}

/**
 * Get the status of the group by group id
 * @param state
 * @param groupId
 * @returns
 */
function getNotifyStatusByGroupId(
  state: NotifyState,
  group: IAlertGroup,
): PortalAccessCheckboxStatus {
  return state.notifiedGroups.includes(group)
    ? PortalAccessCheckboxStatus.CHECKED
    : PortalAccessCheckboxStatus.UNCHECKED;
}

const NotifyEmailTable: React.FC<NotifyEmailTableProps> = ({
  notifyState,
  potentialGroups,
  setNotifyStateDispatch,
}) => {
  const { t } = useTranslation("bulletin/share");
  const [showOtherAgencies, setShowOtherAgencies] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // 添加滚动到底部的函数
  const scrollToBottom = () => {
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop =
          tableContainerRef.current.scrollHeight;
      }
    }, 100); // 添加短暂延迟确保DOM已更新
  };

  return (
    <TableContainer
      ref={tableContainerRef}
      sx={{
        maxWidth: "600px",
        maxHeight: "400px  !important",
        border: "none",
        overflowX: "auto",
        overflowY: "auto",
        "@media (max-width: 600px)": {
          maxWidth: "100%",
        },
      }}
    >
      <Table
        sx={{
          maxWidth: "600px",
          maxHeight: "400px  !important",
          borderCollapse: "separate",
          borderSpacing: 0,
          "& td, & th": {
            borderRight: "none",
            borderLeft: "none",
            borderBottom: "none",
            "@media (max-width: 600px)": {
              display: "block",
              width: "100%",
              textAlign: "left",
              padding: "8px",
            },
          },
          "& thead": {
            borderTop: "none",
            "& th": {
              backgroundColor: "white!important",
              color: "black!important",
              borderTop: "none",
              "@media (max-width: 600px)": {
                display: "none",
              },
            },
          },
          "@media (max-width: 600px)": {
            display: "block",
            "& tbody": {
              display: "block",
              width: "100%",
            },
            "& tr": {
              display: "block",
              marginBottom: "16px",
              border: "1px solid var(--divider)",
              borderRadius: "4px",
            },
          },
        }}
        aria-label="simple table"
      >
        <TableHead sx={{ backgroundColor: "white!important" }}>
          <TableRow>
            <TableCell></TableCell>
            <TableCell align="left">
              <div className="px-2 d-flex justify-content-start gap-2">
                {t("notify")}
                <Tooltip title={t("notify_tooltip")}>
                  <Info />
                </Tooltip>
              </div>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          <TableRow key="myself" hover sx={{ "& td": { py: 1 } }}>
            <TableCell component="th" scope="row">
              {t("myself")}
            </TableCell>
            <TableCell align="left">
              <Checkbox
                color="secondary"
                onChange={() => {
                  setNotifyStateDispatch({
                    type: "SET_INCLUDE_MYSELF",
                  });
                }}
                checked={notifyState.includeMyself}
              />
            </TableCell>
          </TableRow>

          {potentialGroups.userBelongedGroups.map((group) => (
            <TableRow key={group.id} hover sx={{ "& td": { py: 1 } }}>
              <TableCell component="th" scope="row">
                <a
                  href={`/rubialert/manage-groups/${group.id}/members`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {group.name}
                </a>
              </TableCell>
              <TableCell align="left">
                <Checkbox
                  color="secondary"
                  onChange={() =>
                    setNotifyStateDispatch({
                      type: "TOGGLE_GROUP_NOTIFY",
                      group: group,
                    })
                  }
                  checked={
                    getNotifyStatusByGroupId(notifyState, group) ===
                    PortalAccessCheckboxStatus.CHECKED
                  }
                />
              </TableCell>
            </TableRow>
          ))}
          <TableRow hover sx={{ "& td": { py: 1 } }}>
            <TableCell component="th" scope="row">
              <div>
                <div>{t("other_agencies")}</div>
                <div className="mt-2">
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => {
                      setShowOtherAgencies(!showOtherAgencies);
                      if (!showOtherAgencies) {
                        scrollToBottom();
                      }
                    }}
                  >
                    {t("advanced")}
                  </Button>
                </div>
              </div>
            </TableCell>
            <TableCell align="left"></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default NotifyEmailTable;
